import * as React from 'react';

import { BarcodeEntity, BarcodeSyncStatus } from 'root/apps/InventoryCountScanQueue/BarcodeService';
import { useBarcodeService } from 'root/apps/InventoryCountScanQueue/useBarcodeService';

export interface ScanDataReturn {
  failedCollection: BarcodeEntity[];
  pendingCollection: BarcodeEntity[];
  doneCollection: BarcodeEntity[];
  addBarcodeScan: (barcode: string) => Promise<void>;
  syncPending: () => Promise<void>;
  purgeByStatus: (status?: BarcodeSyncStatus | BarcodeSyncStatus[]) => Promise<void>;
  updateCollections: () => Promise<void>;
}

export const ScanDataContext = React.createContext<ScanDataReturn>({} as ScanDataReturn);

interface ScanDataProviderProps {
  accountID: string;
  employeeID: string;
  inventoryCountID: string;
  failedCollection?: BarcodeEntity[];
  pendingCollection?: BarcodeEntity[];
  doneCollection?: BarcodeEntity[];
}

export const ScanDataProvider: React.FC<ScanDataProviderProps> = ({
  accountID,
  employeeID,
  inventoryCountID,
  failedCollection: _failedCollection,
  pendingCollection: _pendingCollection,
  doneCollection: _doneCollection,
  children,
}) => {
  const [failedCollection, setFailedCollection] = React.useState<BarcodeEntity[]>(
    _failedCollection || []
  );
  const [pendingCollection, setPendingCollection] = React.useState<BarcodeEntity[]>(
    _pendingCollection || []
  );
  const [doneCollection, setDoneCollection] = React.useState<BarcodeEntity[]>(
    _doneCollection || []
  );
  const barcodeService = useBarcodeService(accountID, employeeID, inventoryCountID);

  const addBarcodeScan = async (barcode: string): Promise<void> => {
    await barcodeService.addBarcode(barcode);
  };

  const syncPending = async (): Promise<void> => {
    if (!window.navigator.onLine) return;
    await barcodeService.syncBarcodes();
  };

  const purgeByStatus = async (status?: BarcodeSyncStatus | BarcodeSyncStatus[]): Promise<void> => {
    if (!status) {
      await barcodeService.deleteBarcodes();
      return;
    }
    const statuses = Array.isArray(status) ? status : [status];
    await Promise.all(statuses.map((status) => barcodeService.deleteBarcodes(status)));
  };

  /**
   * Update the collections of barcodes
   * We expect consumers to call this method in useEffect, therefore we use useCallback to memoize the function
   */
  const updateCollections = React.useCallback(async (): Promise<void> => {
    let failed: BarcodeEntity[] = [];
    let pending: BarcodeEntity[] = [];
    let done: BarcodeEntity[] = [];
    await Promise.all([
      barcodeService.getBarcodes('failed').then((_failed) => {
        failed = _failed;
      }),
      barcodeService.getBarcodes('pending').then((_pending) => {
        pending = _pending;
      }),
      barcodeService.getBarcodes('done').then((_done) => {
        done = _done;
      }),
    ]);
    setFailedCollection(failed);
    setPendingCollection(pending);
    setDoneCollection(done);
  }, [barcodeService, setFailedCollection, setPendingCollection, setDoneCollection]);

  return (
    <ScanDataContext.Provider
      value={{
        addBarcodeScan,
        syncPending,
        purgeByStatus,
        updateCollections,
        failedCollection,
        pendingCollection,
        doneCollection,
      }}
    >
      {children}
    </ScanDataContext.Provider>
  );
};
